// PlaybookRow — eine Karte in der Playbook-Liste (Titel, Trigger-Chips,
// Antwort-Vorschau, Aktiv-Schalter, Bearbeiten/Löschen). Inaktive gedimmt.

import { Pencil, Trash2, CornerUpLeft } from 'lucide-react';
import type { AdminPlaybook } from '@/lib/admin-playbooks-api';

interface PlaybookRowProps {
  playbook: AdminPlaybook;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}

export function PlaybookRow({ playbook, onEdit, onToggle, onDelete }: PlaybookRowProps) {
  const { title, triggers, answer, active } = playbook;

  return (
    <div
      className={[
        'glass-strong rounded-2xl border border-white/5 p-4 transition hover:border-amber-500/20',
        active ? '' : 'opacity-55',
      ].join(' ')}
    >
      <div className="flex items-start gap-4">
        {/* Inhalt */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[14px] font-semibold text-neutral-100">{title}</h3>
            {active ? (
              <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                Aktiv
              </span>
            ) : (
              <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-neutral-400">
                Inaktiv
              </span>
            )}
            {playbook.attachmentKey ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-medium text-neutral-400">
                PDF
              </span>
            ) : null}
          </div>

          {triggers.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {triggers.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium text-amber-200/90"
                >
                  <CornerUpLeft className="size-3 opacity-70" />
                  {t}
                </span>
              ))}
            </div>
          ) : null}

          <p className="mt-2.5 line-clamp-2 max-w-2xl text-[12px] leading-relaxed text-neutral-400">
            {answer}
          </p>
        </div>

        {/* Toggle + Aktionen */}
        <div className="flex shrink-0 flex-col items-end gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={active}
            aria-label="Aktiv"
            onClick={onToggle}
            className={[
              'relative inline-flex h-[22px] w-10 shrink-0 rounded-full transition-colors',
              active ? 'bg-amber-400/85' : 'bg-white/10',
            ].join(' ')}
          >
            <span
              className={[
                'absolute top-0.5 left-0.5 size-[18px] rounded-full shadow transition-transform',
                active ? 'translate-x-[18px] bg-[#1a1300]' : 'bg-neutral-200',
              ].join(' ')}
            />
          </button>

          <div className="flex items-center gap-1">
            <button
              type="button"
              title="Bearbeiten"
              onClick={onEdit}
              className="rounded-lg border border-white/10 p-1.5 text-neutral-400 transition hover:border-amber-500/40 hover:text-amber-300"
            >
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              title="Löschen"
              onClick={onDelete}
              className="rounded-lg border border-white/10 p-1.5 text-neutral-400 transition hover:border-rose-500/40 hover:text-rose-300"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
